import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  FileInput,
  FileTokenGroup,
  Icon,
  Link,
  PromptInput,
  StatusIndicator,
} from '@amzn/awsui-components-console';
import { ChatBubble } from '@amzn/awsui-chat-components-console';
import { useWebSocketConnection } from './useWebSocketConnection';
import { useAgentWebSocket } from './useAgentWebSocket';
import { useThreadManager } from './useThreadManager';
import AgentMessageList from './AgentMessageList';
import ChatHistorySidebar from './ChatHistorySidebar';
import ChatBubbleAvatar from '../ChatInterface/ChatBubbleAvatar';
import { uploadFileToS3 } from '../../../utils/s3Upload';
import type { ChatInterfaceV2Props, AgentChatMessage } from './types';
import './ChatInterfaceV2.scss';

const SUGGESTION_CHIPS = [
  'Find tables and check my access',
  'Build a use-case definition for code generation',
  'Run and monitor data pipeline jobs',
  'Upload an Excel file with data mappings',
];

const ChatInterfaceV2: React.FC<ChatInterfaceV2Props> = ({
  profileName,
  profileId,
  userName = 'User',
  userInitial = 'U',
}) => {
  const [prompt, setPrompt] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  const connection = useWebSocketConnection();
  const { isConnected, connectionError } = connection;

  const sessionIdRef = useRef<string | null>(null);

  const {
    messages,
    streamState,
    isStreaming,
    isWaiting,
    error: chatError,
    sendMessage,
    sendStructured,
    clearMessages,
    setMessages,
  } = useAgentWebSocket({ profileId, sessionIdRef, connection });

  const handleThreadSelected = useCallback(
    (historyMessages: AgentChatMessage[]) => {
      setMessages(historyMessages);
    },
    [setMessages],
  );

  const threadManager = useThreadManager({
    profileId,
    connection,
    onThreadSelected: handleThreadSelected,
  });

  sessionIdRef.current = threadManager.activeSessionId;

  const error = chatError || connectionError;

  const UPLOAD_KEYWORDS = /upload|excel|xlsx|xls|spreadsheet|file.*mapping/i;

  const isUploadIntent = useCallback((text: string) => UPLOAD_KEYWORDS.test(text), []);

  const handleFileUploadAndProcess = useCallback(async (file: File) => {
    setIsUploading(true);
    setShowFileUpload(false);
    setFiles([]);

    const uploadingMsg: AgentChatMessage = {
      id: `uploading-${Date.now()}`,
      role: 'assistant',
      content: `Uploading **${file.name}**...`,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, uploadingMsg]);

    try {
      const s3Key = await uploadFileToS3(file);

      setMessages(prev =>
        prev.filter(m => !m.id.startsWith('uploading-')).concat({
          id: `upload-ok-${Date.now()}`,
          role: 'assistant',
          content: `File **${file.name}** uploaded successfully! Now generating template...`,
          timestamp: new Date(),
        }),
      );

      sendStructured({
        type: 'excelUpload',
        excelSheetUri: s3Key,
        fileName: file.name,
      });
    } catch (err: any) {
      setMessages(prev =>
        prev.filter(m => !m.id.startsWith('uploading-')).concat({
          id: `upload-err-${Date.now()}`,
          role: 'assistant',
          content: `Failed to upload file: ${err.message || 'Unknown error'}. Please try again.`,
          timestamp: new Date(),
        }),
      );
      setShowFileUpload(true);
    } finally {
      setIsUploading(false);
    }
  }, [sendStructured, setMessages]);

  const handleSend = useCallback(() => {
    if (files.length > 0) {
      handleFileUploadAndProcess(files[0]);
      return;
    }

    const trimmed = prompt.trim();
    if (!trimmed || isStreaming || isWaiting) return;

    if (!threadManager.activeSessionId) {
      threadManager.createNewThread();
    }

    if (isUploadIntent(trimmed)) {
      setShowFileUpload(true);
      setMessages(prev => [
        ...prev,
        { id: `user-${Date.now()}`, role: 'user', content: trimmed, timestamp: new Date() },
        {
          id: `upload-prompt-${Date.now()}`,
          role: 'assistant',
          content: 'Please select an Excel file (.xlsx, .xls) using the attachment button below, then press send to upload.',
          timestamp: new Date(),
        },
      ]);
      setPrompt('');
      return;
    }

    sendMessage(trimmed);
    setPrompt('');
  }, [prompt, files, isStreaming, isWaiting, sendMessage, threadManager, isUploadIntent, handleFileUploadAndProcess, setMessages]);

  const handleChipClick = useCallback(
    (text: string) => {
      if (isStreaming || isWaiting) return;
      if (!threadManager.activeSessionId) {
        threadManager.createNewThread();
      }

      if (isUploadIntent(text)) {
        setShowFileUpload(true);

        setMessages(prev => [
          ...prev,
          { id: `user-${Date.now()}`, role: 'user', content: text, timestamp: new Date() },
          {
            id: `upload-prompt-${Date.now()}`,
            role: 'assistant',
            content: 'Please select an Excel file (.xlsx, .xls) using the attachment button below.',
            timestamp: new Date(),
          },
        ]);
        return;
      }

      sendMessage(text);
    },
    [isStreaming, isWaiting, sendMessage, threadManager, isUploadIntent, setMessages],
  );

  const handleAction = useCallback(
    (action: string) => {
      if (action.startsWith('http://') || action.startsWith('https://')) {
        window.open(action, '_blank');
        return;
      }
      if (action === 'submitAccessRequest') {
        sendStructured({ type: 'formSubmit', formAction: 'submitAccessRequest' });
        return;
      }
      if (action.startsWith('approve_artifacts')) {
        const jobId = action.includes(':') ? action.split(':')[1] : '';
        const approveMsg = jobId
          ? `I approve the generated artifacts for job ${jobId}. Please name and finalize this job.`
          : 'I approve the generated artifacts. Please name and finalize this job.';
        sendStructured({ type: 'approveArtifacts', jobId, message: approveMsg });
        return;
      }
      if (action === 'cancelAccessRequest') return;
      sendMessage(action);
    },
    [sendMessage, sendStructured],
  );

  const handleNewThread = useCallback(() => {
    threadManager.createNewThread();
    clearMessages();
    setShowHistory(false);
    setShowFileUpload(false);
    setFiles([]);
    setIsUploading(false);
  }, [threadManager.createNewThread, clearMessages]);

  const handleSelectThread = useCallback(
    (sessionId: string) => {
      threadManager.selectThread(sessionId);
      setShowHistory(false);
      setShowFileUpload(false);
      setFiles([]);
      setIsUploading(false);
    },
    [threadManager.selectThread],
  );

  const handleToggleHistory = useCallback(() => {
    setShowHistory(prev => {
      if (!prev) {
        threadManager.fetchThreads();
      }
      return !prev;
    });
  }, [threadManager.fetchThreads]);

  const handleDeleteThread = useCallback(
    (_sessionId: string) => {
      // TODO: wire to backend delete when available
    },
    [],
  );

  const hasMessages = messages.length > 0 || isStreaming;

  return (
    <div
      ref={chatContainerRef}
      className={`chat-interface-v2 ${isFullscreen ? 'chat-v2-fullscreen' : ''}`}
      data-testid="chat-interface-v2"
    >
      {showHistory && (
        <ChatHistorySidebar
          threads={threadManager.threads}
          activeSessionId={threadManager.activeSessionId}
          loading={threadManager.loadingThreads}
          onSelectThread={handleSelectThread}
          onDeleteThread={handleDeleteThread}
          onClose={() => setShowHistory(false)}
        />
      )}

      <div className="chat-v2-card">
        {/* Header bar */}
        <div className="chat-v2-header">
          <div className="chat-v2-header-left">
            <span className="chat-v2-title">ADDAPT Chat</span>
            <span
              className={`chat-v2-status-dot ${isConnected ? 'connected' : 'connecting'}`}
              title={isConnected ? 'Connected' : 'Connecting...'}
            />
          </div>
          <div className="chat-v2-header-actions">
            <button
              className="chat-v2-icon-btn"
              onClick={handleNewThread}
              title="New chat"
              type="button"
            >
              <Icon name="add-plus" />
            </button>
            <button
              className="chat-v2-icon-btn"
              onClick={handleToggleHistory}
              title="Chat history"
              type="button"
            >
              <Icon name="status-pending" />
            </button>
            <button
              className="chat-v2-icon-btn"
              onClick={() => setIsFullscreen(f => !f)}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              type="button"
            >
              <Icon name={isFullscreen ? 'shrink' : 'expand'} />
            </button>
          </div>
        </div>

        {/* Connection / error banner */}
        {error && (
          <div className="chat-v2-error-banner">
            <StatusIndicator type="error">{error}</StatusIndicator>
          </div>
        )}

        {/* Messages area */}
        <div className="chat-v2-body">
          {!hasMessages && !threadManager.loadingHistory ? (
            <div className="chat-v2-welcome">
              <ChatBubble
                avatar={<ChatBubbleAvatar type="assistant" name="ADDAPT Intelligence" />}
                ariaLabel="ADDAPT Intelligence welcome message"
                type="incoming"
              >
                <div className="chat-v2-welcome-text">
                  <strong>Hi{userName && userName !== 'User' ? `, ${userName}` : ''}! I'm ADDAPT</strong> — your data pipeline assistant.
                  <br /><br />
                  I can help you with:
                </div>
              </ChatBubble>

              <div className="chat-v2-chips">
                {SUGGESTION_CHIPS.map(chip => (
                  <button
                    key={chip}
                    className="chat-v2-chip"
                    onClick={() => handleChipClick(chip)}
                    disabled={!isConnected || isStreaming || isWaiting}
                    type="button"
                  >
                    {chip}
                  </button>
                ))}
              </div>

              <div className="chat-v2-welcome-prompt">
                How you want to get started?
              </div>
            </div>
          ) : (
            <>
              {threadManager.loadingHistory && messages.length === 0 && (
                <Box textAlign="center" padding={{ top: 'xl' }}>
                  <StatusIndicator type="loading">Loading conversation...</StatusIndicator>
                </Box>
              )}
              <AgentMessageList
                messages={messages}
                streamState={streamState}
                isWaiting={isWaiting}
                userName={userName}
                userInitial={userInitial}
                onAction={handleAction}
              />
            </>
          )}
        </div>

        {/* Input area */}
        <div className="chat-v2-input-area">
          <PromptInput
            ref={promptInputRef}
            onChange={({ detail }) => setPrompt(detail.value)}
            onAction={handleSend}
            value={prompt}
            actionButtonAriaLabel={isStreaming || isWaiting || isUploading ? 'Thinking...' : 'Send message'}
            actionButtonIconName="send"
            ariaLabel="Chat input"
            placeholder={
              isUploading
                ? 'Uploading file...'
                : showFileUpload
                  ? 'Select an Excel file and press send'
                  : !isConnected
                    ? 'Connecting...'
                    : 'Ask a question'
            }
            disabled={!isConnected || isStreaming || isWaiting || isUploading}
            autoFocus
            disableSecondaryActionsPaddings
            secondaryActions={
              showFileUpload ? (
                <Box padding={{ left: 'xxs', top: 'xs' }}>
                  <FileInput
                    ariaLabel="Upload Excel file"
                    variant="icon"
                    multiple={false}
                    accept=".xlsx,.xls"
                    value={files}
                    onChange={({ detail }) => {
                      if (detail.value.length > 0) {
                        setFiles([detail.value[0]]);
                      }
                    }}
                  />
                </Box>
              ) : undefined
            }
            secondaryContent={
              files.length > 0 ? (
                <Box>
                  <FileTokenGroup
                    items={files.map(file => ({ file }))}
                    onDismiss={() => {
                      setFiles([]);
                      promptInputRef.current?.focus();
                    }}
                    limit={1}
                    alignment="horizontal"
                  />
                </Box>
              ) : undefined
            }
          />
        </div>

        {/* Footer disclaimer */}
        <div className="chat-v2-footer-disclaimer">
          ADDAPT uses generative AI. You may need to verify responses for accuracy.{' '}
          <Link href="https://aws.amazon.com/machine-learning/responsible-ai/policy/" external>
            AWS Responsible AI Policy
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChatInterfaceV2;
